import React, { FC, ReactNode, createContext, memo, useContext, useMemo, useState, useRef, Dispatch, useEffect } from 'react';
import { ROUTES, RouteItem } from './types';

type HistoryItem = {
  path: ROUTES;
};

const noop = () => {};

export const RouterContext = createContext({
  history: [] as HistoryItem[],
  goToRoute: noop as (path: ROUTES, anchor?: string) => void,
  registerRoute: noop as (routeItem: RouteItem) => void,
  routes: new Map<ROUTES, ReactNode>(),
  currentRoute: null as ROUTES | null,
  setCurrentRoute: noop as Dispatch<React.SetStateAction<ROUTES | null>>,
  isLoading: false,
  loadingText: '',
  setLoadingText: noop as Dispatch<React.SetStateAction<string>>,
  setIsLoading: noop as Dispatch<React.SetStateAction<boolean>>,
  goBack: noop as () => void,
  setError: noop as Dispatch<React.SetStateAction<string>>,
  error: '',
});

export const useRouter = () => {
  const context = useContext(RouterContext);
  if (context === undefined) {
    throw new Error('useRouter must be used within a RouterProvider');
  }

  return context;
};

type RouterProviderProps = {
  children: ReactNode | ReactNode[];
};

const DEFAULT_LOADING_TEXT = 'Loading...';

export const RouterProvider: FC<RouterProviderProps> = memo(({ children }: any) => {
  const [currentRoute, setCurrentRoute] = useState<ROUTES | null>(ROUTES.ASR_FORM);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingText, setLoadingText] = useState<string>(DEFAULT_LOADING_TEXT);
  const [error, setError] = useState<string>('');
  const routes = useRef<Map<ROUTES, ReactNode>>(new Map());
  const history = useRef<HistoryItem[]>([]);

  useEffect(() => {
    if (isLoading) {
      goToRoute(ROUTES.LOADING);
    } else {
      setLoadingText(DEFAULT_LOADING_TEXT);
    }
  }, [isLoading]);

  const goToRoute = (path: ROUTES) => {
    const route = routes.current.get(path);
    if (!route && process.env.LOCAL !== 'true') {
      console.log(`Route not found ${path}`);
      return;
    }

    setCurrentRoute(path);
    history.current.push({ path });
  };

  const registerRoute = (routeItem: RouteItem) => {
    routes.current.set(routeItem.path, routeItem.component);
  };

  const goBack = () => {
    if (history.current.length < 2) return;
    const previousRoute = history.current[history.current.length - 2];
    if (previousRoute) {
      history.current = history.current.slice(0, history.current.length - 1);
      setCurrentRoute(previousRoute.path);
    }
  };

  const value = useMemo(
    () => ({
      history: history.current,
      goToRoute,
      registerRoute,
      routes: routes.current,
      currentRoute,
      setCurrentRoute,
      isLoading,
      setIsLoading,
      goBack,
      error,
      setError,
      loadingText,
      setLoadingText,
    }),
    [
      currentRoute,
      error,
    ]
  );

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
});
